import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ArrowDownCircle, ArrowUpCircle, Gift, Dices, Trophy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import {
  getUserTransactions,
  formatTransactionType,
  formatTransactionStatus,
  getStatusColor,
  type Transaction,
  type TransactionType,
} from '../services/transactionService'
import { formatCurrency } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface HistoryModalProps {
  isOpen: boolean
  onClose: () => void
}

const getTransactionIcon = (type: TransactionType) => {
  switch (type) {
    case 'deposit':
      return <ArrowDownCircle className="h-5 w-5 text-green-500" />
    case 'withdrawal':
      return <ArrowUpCircle className="h-5 w-5 text-red-500" />
    case 'bet':
      return <Dices className="h-5 w-5 text-blue-500" />
    case 'win':
      return <Trophy className="h-5 w-5 text-casino-gold" />
    case 'bonus':
      return <Gift className="h-5 w-5 text-purple-500" />
    default:
      return null
  }
}

function TransactionItem({ transaction }: { transaction: Transaction }) {
  const isPositive = ['deposit', 'win', 'bonus'].includes(transaction.type)

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between p-4 bg-casino-black/30 rounded-lg"
    >
      <div className="flex items-center gap-3">
        {getTransactionIcon(transaction.type)}
        <div>
          <p className="font-medium text-white">
            {formatTransactionType(transaction.type)}
          </p>
          <p className="text-xs text-gray-500">
            {transaction.createdAt.toLocaleDateString('es-AR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={cn('font-bold', isPositive ? 'text-green-500' : 'text-red-500')}>
          {isPositive ? '+' : '-'}{formatCurrency(transaction.amount)}
        </p>
        <p className={cn('text-xs', getStatusColor(transaction.status))}>
          {formatTransactionStatus(transaction.status)}
        </p>
      </div>
    </motion.div>
  )
}

export function HistoryModal({ isOpen, onClose }: HistoryModalProps) {
  const { user, isDemo } = useAuth()
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')

  useEffect(() => {
    const loadTransactions = async () => {
      if (!user || isDemo) {
        setTransactions([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      const data = await getUserTransactions(user.uid)
      setTransactions(data)
      setIsLoading(false)
    }

    if (isOpen) {
      loadTransactions()
    }
  }, [user, isOpen, isDemo])

  const filteredTransactions = transactions.filter((t) => {
    if (activeTab === 'all') return true
    if (activeTab === 'deposits') return t.type === 'deposit'
    if (activeTab === 'withdrawals') return t.type === 'withdrawal'
    if (activeTab === 'games') return ['bet', 'win'].includes(t.type)
    return true
  })

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-casino-black-light border-casino-gold/30 sm:max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-2xl text-casino-gold text-center">
            Historial
          </DialogTitle>
        </DialogHeader>

        {isDemo ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-xl text-casino-gold mb-2">Modo Demo</h3>
            <p className="text-gray-400 mb-6">
              El historial de transacciones no está disponible en modo demo.
              Crea una cuenta para acceder a todas las funciones.
            </p>
            <Button variant="outline" onClick={onClose}>
              Entendido
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="all">Todo</TabsTrigger>
                <TabsTrigger value="deposits">Depósitos</TabsTrigger>
                <TabsTrigger value="withdrawals">Retiros</TabsTrigger>
                <TabsTrigger value="games">Juegos</TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="mt-4">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-casino-gold border-t-transparent rounded-full mx-auto" />
                    <p className="text-gray-400 mt-4">Cargando...</p>
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">📋</div>
                    <p className="text-gray-400">No hay transacciones</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {filteredTransactions.map((transaction) => (
                      <TransactionItem key={transaction.id} transaction={transaction} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
